import { type FC, useState } from 'react'
import classNames from 'classnames'
import { Modal } from '../../../../components/Modal/Modal'
import { Button } from '../../../../components/Button/Button'
import { PastDuePolicy, ReminderType } from '../../../../models/Scheduling'

interface PastDueOption {
  value: PastDuePolicy
  label: string
  description: string
}

interface PastDueCopy {
  title: string
  body: string
  options: PastDueOption[]
}

const buildPastDueCopy = (reminderType: ReminderType, pastDueCount: number): PastDueCopy => {
  const noun = reminderType === ReminderType.FOLLOW_UP ? 'follow-up' : 'reminder'
  const countedNoun = pastDueCount === 1 ? `${pastDueCount} ${noun}` : `${pastDueCount} ${noun}s`
  return {
    title: `Send ${countedNoun} now?`,
    body: `You set a ${noun} to send sooner, so ${pastDueCount} ${noun} email${pastDueCount === 1 ? ' is' : 's are'} now due.`,
    options: [
      {
        value: PastDuePolicy.SEND,
        label: `Send the ${countedNoun}`,
        description: `Clients receive ${pastDueCount === 1 ? 'it' : 'them'} within 30 minutes of saving.`,
      },
      {
        value: PastDuePolicy.SKIP,
        label: `Skip the ${countedNoun}`,
        description: `Nothing is sent now. Later ${noun}s still go out as normal.`,
      },
    ],
  }
}

interface PastDueOptionCardProps {
  option: PastDueOption
  checked: boolean
  onSelect: (value: PastDuePolicy) => void
}

const PastDueOptionCard: FC<PastDueOptionCardProps> = ({ option, checked, onSelect }) => (
  <label
    htmlFor={`past-due-${option.value}`}
    className={classNames('d-flex align-items-center gap-2 p-3 border rounded-3 w-100', checked ? 'zocks-border-primary' : 'zocks-border-color-gray-300')}
  >
    <input
      type='radio'
      id={`past-due-${option.value}`}
      name='past-due-policy'
      value={option.value}
      checked={checked}
      onChange={() => onSelect(option.value)}
      className='form-check-input flex-shrink-0 mt-0'
    />
    <div className='d-flex flex-column flex-grow-1'>
      <span className='paragraph-medium'>{option.label}</span>
      <span className='paragraph-small zocks-text-gray-600'>{option.description}</span>
    </div>
  </label>
)

export interface PastDueConfirmModalProps {
  reminderType: ReminderType
  pastDueCount: number
  isSaving: boolean
  onConfirm: (pastDuePolicy: PastDuePolicy) => void
  onCancel: () => void
}

export const PastDueConfirmModal: FC<PastDueConfirmModalProps> = ({ reminderType, pastDueCount, isSaving, onConfirm, onCancel }) => {
  const [pastDuePolicy, setPastDuePolicy] = useState<PastDuePolicy>(PastDuePolicy.SEND)
  const copy = buildPastDueCopy(reminderType, pastDueCount)
  return (
    <Modal
      show
      footerClassName='p-4 pt-0'
      onHide={onCancel}
      header={<h5 className='mb-0'>{copy.title}</h5>}
      headerBodySeparator
      body={
        <div className='d-flex flex-column gap-4'>
          <div className='paragraph-medium'>{copy.body}</div>
          <div className='d-flex flex-column gap-2'>
            {copy.options.map((option) => (
              <PastDueOptionCard key={option.value} option={option} checked={pastDuePolicy === option.value} onSelect={setPastDuePolicy} />
            ))}
          </div>
        </div>
      }
      footer={
        <div className='d-flex zocks-gap-0.75 justify-content-end w-100'>
          <Button variant='secondary' onClick={onCancel}>
            Cancel
          </Button>
          <Button variant='primary' loading={isSaving} onClick={() => onConfirm(pastDuePolicy)}>
            Save changes
          </Button>
        </div>
      }
    />
  )
}
